// @catalog F.006
// @catalog F.030
// DrillPanel — slide-in lateral 420px abierto al click en una fila de
// RankingBars. Usa MUI Drawer anchor="right" (NO es el shell — el shell
// es MenuDrawer y queda intacto).
import type { ReactNode } from "react";
import { Box, Drawer, IconButton, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface DrillPanelProps {
  open: boolean;
  title: string;
  subtitle?: string | undefined;
  onClose: () => void;
  children?: ReactNode;
}

export function DrillPanel(props: DrillPanelProps): JSX.Element {
  const { open, title, subtitle, onClose, children } = props;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        sx: {
          width: 420,
          maxWidth: "100vw",
          backgroundColor: "var(--surface)",
          borderLeft: "1px solid var(--border)",
          boxShadow: "-20px 0 50px rgba(13, 18, 32, 0.06)",
        },
      }}
    >
      <Box
        sx={{
          padding: "16px 18px",
          borderBottom: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: "var(--ink-1)", lineHeight: 1.2 }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{ color: "var(--ink-3)", fontSize: "12px" }}
            >
              {subtitle}
            </Typography>
          )}
        </Stack>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Cerrar panel"
          sx={{
            color: "var(--ink-3)",
            "&:hover": { backgroundColor: "var(--bg-2)", color: "var(--ink-1)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ padding: "18px", overflowY: "auto", flex: 1 }}>{children}</Box>
    </Drawer>
  );
}

// Bloque interno reutilizable para mostrar grupos stat 2x2 en el body del panel.
interface StatGridProps {
  items: ReadonlyArray<{ label: string; value: string; helper?: string | undefined }>;
}

export function StatGrid(props: StatGridProps): JSX.Element {
  const { items } = props;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "12px",
        marginBottom: "16px",
      }}
    >
      {items.map((it) => (
        <Box
          key={it.label}
          sx={{
            backgroundColor: "var(--bg)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: "10px 12px",
          }}
        >
          <Box
            sx={{
              fontSize: "11px",
              color: "var(--ink-3)",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              fontWeight: 500,
            }}
          >
            {it.label}
          </Box>
          <Box
            sx={{
              marginTop: "4px",
              fontSize: "16px",
              fontWeight: 600,
              color: "var(--ink-1)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {it.value}
          </Box>
          {it.helper && (
            <Box sx={{ fontSize: "11.5px", color: "var(--ink-3)", marginTop: "2px" }}>
              {it.helper}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}
